import { bigMath } from "../../misc";
import { ObjectLike, OrderInputLike, OrderItemInputLike } from "../../types";
import { Offer } from "~served/types/db";

type GenerateBillTemplateParametersOrder = OrderInputLike<{
	note?: UndefinedOrNull<string>;
}>;

interface DuplicateItemResponse {
	isDuplicate: boolean;
	matchedIndex: number;
	returnItem: OrderItemInputLike;
}

export const getCombinedNotes = (
	orders: { note?: UndefinedOrNull<string> }[],
) =>
	orders
		.map((o) => o.note)
		.filter(Boolean)
		.join(", ");

export const getCombinedOffers = (items: OrderItemInputLike[]) =>
	items.reduce(
		(pre, cur) => {
			if (
				cur.subtotal_addons?.offer?.amount &&
				cur.subtotal_addons.offer.metadata?._id
			) {
				const newArray = [...pre];
				const offerIndex = newArray.findIndex(
					(p) =>
						p.metadata._id.toString() ===
						(cur.subtotal_addons!.offer.metadata as Offer)._id.toString(),
				);
				if (offerIndex !== -1) {
					newArray[offerIndex].amount = bigMath.add(
						newArray[offerIndex].amount,
						cur.subtotal_addons.offer.amount,
					);
				} else {
					newArray.push({
						metadata: cur.subtotal_addons.offer.metadata,
						amount: cur.subtotal_addons.offer.amount,
					});
				}

				return newArray;
			}

			return pre;
		},
		[] as { metadata: ObjectLike; amount: number }[],
	);

interface CourseIndexedItem {
	course_index?: number | null;
}

const getCourseIndex = (item?: CourseIndexedItem) =>
	typeof item?.course_index === "number" ? item.course_index : undefined;

export const hasCourseBoundary = (
	left?: CourseIndexedItem,
	right?: CourseIndexedItem,
) => {
	const leftCourseIndex = getCourseIndex(left);
	const rightCourseIndex = getCourseIndex(right);

	return (
		leftCourseIndex !== undefined &&
		rightCourseIndex !== undefined &&
		leftCourseIndex !== rightCourseIndex
	);
};

export const getCourseBoundaryIndexes = (items: CourseIndexedItem[]) =>
	items.flatMap((item, index) =>
		hasCourseBoundary(item, items[index + 1]) ? [index] : [],
	);

export const getMaxCourseIndex = (items: CourseIndexedItem[]) =>
	items.reduce((max, item) => {
		const courseIndex = getCourseIndex(item);
		return courseIndex === undefined ? max : Math.max(max, courseIndex);
	}, -1);

export const getNextCourseIndex = (items: CourseIndexedItem[]) =>
	getMaxCourseIndex(items) + 1;

export const mergeCourseIntoPrevious = <
	TItem extends { course_index?: number | null },
>(
	items: TItem[],
	lowerCourseIndex: number,
) => {
	const lowerCourseFirstIndex = items.findIndex(
		(item) => item.course_index === lowerCourseIndex,
	);
	if (lowerCourseFirstIndex <= 0) return items;

	const previousCourseIndex = items
		.slice(0, lowerCourseFirstIndex)
		.reverse()
		.find((item) => typeof item.course_index === "number")?.course_index;
	if (previousCourseIndex === undefined) return items;

	return items.map((item) =>
		item.course_index === lowerCourseIndex
			? { ...item, course_index: previousCourseIndex }
			: item,
	);
};

export const getCombinedOrdersItems = (
	orders: GenerateBillTemplateParametersOrder[],
) => {
	const combinedItems: OrderItemInputLike[] = [];
	orders.map((o) => {
		o.items.map((item) => {
			// Master instances and their children must retain their original IDs
			// until groupItemsByMaster reconstructs each composition. Combining
			// them here discards master_parent ownership and produces orphans.
			if (item.is_master || item.master_parent) {
				combinedItems.push(item);
				return;
			}
			const duplicate = getDuplicateItem(combinedItems, item);
			const { isDuplicate, matchedIndex, returnItem } = duplicate;
			if (isDuplicate && matchedIndex !== -1) {
				combinedItems[matchedIndex] = {
					...returnItem,
					quantity: returnItem.quantity + item.quantity,
					original_price_addons: {
						service_charge: {
							...returnItem.original_price_addons.service_charge,
							amount: bigMath.add(
								returnItem.original_price_addons.service_charge.amount,
								item.original_price_addons.service_charge.amount,
							),
						},
						vat: {
							...returnItem.original_price_addons.vat,
							amount: bigMath.add(
								returnItem.original_price_addons.vat.amount,
								item.original_price_addons.vat.amount,
							),
						},
					},
					subtotal_addons: {
						...returnItem.subtotal_addons,
						offer: {
							...returnItem.subtotal_addons?.offer,
							amount: bigMath.add(
								returnItem.subtotal_addons?.offer.amount ?? 0,
								item.subtotal_addons?.offer.amount ?? 0,
							),
						},
						discount: {
							...returnItem.subtotal_addons?.discount,
							amount: bigMath.add(
								returnItem.subtotal_addons?.discount.amount ?? 0,
								item.subtotal_addons?.discount.amount ?? 0,
							),
						},
					},
					subtotal: bigMath.add(returnItem.subtotal, item.subtotal),
					net_amount: bigMath.add(returnItem.net_amount, item.net_amount),
					gross_amount: bigMath.add(returnItem.gross_amount, item.gross_amount),
					cancelled_amount: bigMath.add(
						returnItem.cancelled_amount,
						item.cancelled_amount,
					),
				};
			} else {
				combinedItems.push(returnItem);
			}
		});
	});
	return combinedItems;
};

export const getCombinedOrdersCancelledItems = (
	orders: GenerateBillTemplateParametersOrder[],
) => {
	const combinedItems: OrderItemInputLike[] = [];
	orders.map((o) => {
		o.cancelled_items?.map((item) => {
			if (item.is_master || item.master_parent) {
				combinedItems.push(item);
				return;
			}
			const duplicate = getDuplicateItem(combinedItems, item);
			const { isDuplicate, matchedIndex, returnItem } = duplicate;
			if (isDuplicate && matchedIndex !== -1) {
				combinedItems[matchedIndex] = {
					...returnItem,
					quantity: returnItem.quantity + item.quantity,
					original_price_addons: {
						service_charge: {
							...returnItem.original_price_addons.service_charge,
							amount: bigMath.add(
								returnItem.original_price_addons.service_charge.amount,
								item.original_price_addons.service_charge.amount,
							),
						},
						vat: {
							...returnItem.original_price_addons.vat,
							amount: bigMath.add(
								returnItem.original_price_addons.vat.amount,
								item.original_price_addons.vat.amount,
							),
						},
					},
					subtotal_addons: {
						...returnItem.subtotal_addons,
						offer: {
							...returnItem.subtotal_addons?.offer,
							amount: bigMath.add(
								returnItem.subtotal_addons?.offer.amount ?? 0,
								item.subtotal_addons?.offer.amount ?? 0,
							),
						},
						discount: {
							...returnItem.subtotal_addons?.discount,
							value: bigMath.add(
								returnItem.subtotal_addons?.discount.value ?? 0,
								item.subtotal_addons?.discount.value ?? 0,
							),
						},
					},
					subtotal: bigMath.add(returnItem.subtotal, item.subtotal),
					net_amount: bigMath.add(returnItem.net_amount, item.net_amount),
					gross_amount: bigMath.add(returnItem.gross_amount, item.gross_amount),
					cancelled_amount: bigMath.add(
						returnItem.cancelled_amount,
						item.cancelled_amount,
					),
				};
			} else {
				combinedItems.push(returnItem);
			}
		});
	});
	return combinedItems;
};

export interface MasterItemGroup {
	master: OrderItemInputLike;
	children: OrderItemInputLike[];
}

export interface GroupedItems {
	masterGroups: MasterItemGroup[];
	regularItems: OrderItemInputLike[];
}

const getId = (value: unknown) => value?.toString?.() ?? String(value ?? "");

const getOfferSignature = (item: OrderItemInputLike) => ({
	id: getId(item.subtotal_addons?.offer?.metadata?._id),
	title: item.subtotal_addons?.offer?.metadata?.title ?? "",
});

const getDiscountSignature = (item: OrderItemInputLike) => ({
	isDivided: item.subtotal_addons?.discount?.is_divided ?? false,
	type: item.subtotal_addons?.discount?.type ?? "",
	value: item.subtotal_addons?.discount?.value ?? 0,
});

const getVisibleOptionSignature = (item: OrderItemInputLike) =>
	item.options
		.filter((option) => option.is_hide_from_receipt === false)
		.map((option) => ({
			option: getId(option.option),
			title: option.title,
			quantity: option.quantity,
			comments: option.comments ?? "",
			listedPrice: option.listed_price,
			originalPrice: option.original_price,
		}));

const getPrintItemSignature = (item: OrderItemInputLike) =>
	JSON.stringify({
		item: getId(item.item),
		title: item.title,
		quantity: item.quantity,
		comments: item.comments ?? "",
		type: item.type,
		isHidden: item.is_hide_from_receipt,
		listedPrice: item.listed_price,
		originalPrice: item.original_price,
		unitPrice: item.unit_price,
		noVat: item.no_vat,
		noServiceCharge: item.no_service_charge,
		offer: getOfferSignature(item),
		discount: getDiscountSignature(item),
		options: getVisibleOptionSignature(item),
	});

const addAmount = (left: unknown, right: unknown) =>
	bigMath.add(Number(left ?? 0), Number(right ?? 0));

const mergeSubtotalAddons = (
	left: OrderItemInputLike["subtotal_addons"],
	right: OrderItemInputLike["subtotal_addons"],
) => ({
	...left,
	offer: {
		...left?.offer,
		amount: addAmount(left?.offer?.amount, right?.offer?.amount),
	},
	discount: {
		...left?.discount,
		amount: addAmount(left?.discount?.amount, right?.discount?.amount),
	},
	vat: {
		...left?.vat,
		amount: addAmount(left?.vat?.amount, right?.vat?.amount),
	},
	service_charge: {
		...left?.service_charge,
		amount: addAmount(
			left?.service_charge?.amount,
			right?.service_charge?.amount,
		),
	},
	service_charge_bd: {
		...left?.service_charge_bd,
		percentage:
			left?.service_charge_bd?.percentage ??
			right?.service_charge_bd?.percentage ??
			0,
		amount: addAmount(
			left?.service_charge_bd?.amount,
			right?.service_charge_bd?.amount,
		),
	},
	tax: {
		...left?.tax,
		amount: addAmount(left?.tax?.amount, right?.tax?.amount),
	},
	card_surcharge: {
		...left?.card_surcharge,
		amount: addAmount(
			left?.card_surcharge?.amount,
			right?.card_surcharge?.amount,
		),
	},
	adjustment: {
		...left?.adjustment,
		amount: addAmount(left?.adjustment?.amount, right?.adjustment?.amount),
	},
});

const mergePrintItems = (
	left: OrderItemInputLike,
	right: OrderItemInputLike,
): OrderItemInputLike => ({
	...left,
	quantity: addAmount(left.quantity, right.quantity),
	serving_quantity: addAmount(left.serving_quantity, right.serving_quantity),
	subtotal: addAmount(left.subtotal, right.subtotal),
	net_amount: addAmount(left.net_amount, right.net_amount),
	gross_amount: addAmount(left.gross_amount, right.gross_amount),
	cancelled_amount: addAmount(left.cancelled_amount, right.cancelled_amount),
	non_sales_credit_amount: addAmount(
		left.non_sales_credit_amount,
		right.non_sales_credit_amount,
	),
	original_price_addons: {
		...left.original_price_addons,
		vat: {
			...left.original_price_addons.vat,
			amount: addAmount(
				left.original_price_addons.vat.amount,
				right.original_price_addons.vat.amount,
			),
		},
		service_charge: {
			...left.original_price_addons.service_charge,
			amount: addAmount(
				left.original_price_addons.service_charge.amount,
				right.original_price_addons.service_charge.amount,
			),
		},
		service_charge_bd: {
			...left.original_price_addons.service_charge_bd,
			percentage:
				left.original_price_addons.service_charge_bd?.percentage ??
				right.original_price_addons.service_charge_bd?.percentage ??
				0,
			amount: addAmount(
				left.original_price_addons.service_charge_bd?.amount,
				right.original_price_addons.service_charge_bd?.amount,
			),
		},
	},
	subtotal_addons: mergeSubtotalAddons(
		left.subtotal_addons,
		right.subtotal_addons,
	),
	options: (() => {
		const rightVisibleOptions = right.options.filter(
			(option) => option.is_hide_from_receipt === false,
		);
		let visibleOptionIndex = 0;
		return left.options.map((option) => {
			if (option.is_hide_from_receipt !== false) return option;
			const rightOption = rightVisibleOptions[visibleOptionIndex++];
			if (!rightOption) return option;
			return {
				...option,
				quantity: addAmount(option.quantity, rightOption.quantity),
				serving_quantity: addAmount(
					option.serving_quantity,
					rightOption.serving_quantity,
				),
				subtotal: addAmount(option.subtotal, rightOption.subtotal),
				net_amount: addAmount(option.net_amount, rightOption.net_amount),
				gross_amount: addAmount(option.gross_amount, rightOption.gross_amount),
				cancelled_amount: addAmount(
					option.cancelled_amount,
					rightOption.cancelled_amount,
				),
			};
		});
	})(),
});

const getMasterGroupSignature = ({ master, children }: MasterItemGroup) =>
	JSON.stringify({
		master: getPrintItemSignature(master),
		children: children.map(getPrintItemSignature),
	});

export const getMasterChildDisplayQuantity = (
	child: Pick<OrderItemInputLike, "quantity">,
) => child.quantity;

export const groupItemsByMaster = (
	items: OrderItemInputLike[],
): GroupedItems => {
	const masters = items.filter((i) => i.is_master === true);
	const children = items.filter((i) => !!i.master_parent);
	const regular = items.filter((i) => !i.is_master && !i.master_parent);

	const reconstructedGroups = masters.map((master) => ({
		master,
		children: children.filter(
			(c) => getId(c.master_parent) === getId(master._id),
		),
	}));
	const masterGroups: MasterItemGroup[] = [];
	const groupIndexesBySignature = new Map<string, number>();

	for (const group of reconstructedGroups) {
		const signature = getMasterGroupSignature(group);
		const existingIndex = groupIndexesBySignature.get(signature);
		if (existingIndex === undefined) {
			groupIndexesBySignature.set(signature, masterGroups.length);
			masterGroups.push(group);
			continue;
		}

		const existing = masterGroups[existingIndex];
		masterGroups[existingIndex] = {
			master: mergePrintItems(existing.master, group.master),
			children: existing.children.map((child, index) =>
				mergePrintItems(child, group.children[index]),
			),
		};
	}

	// Orphaned children (master_parent points to non-existent master) go to regular
	const orphanedChildren = children.filter(
		(c) => !masters.some((m) => getId(m._id) === getId(c.master_parent)),
	);

	return {
		masterGroups,
		regularItems: [...regular, ...orphanedChildren],
	};
};

const getDuplicateItem = (
	itemList: OrderItemInputLike[],
	item: OrderItemInputLike,
) => {
	const response: DuplicateItemResponse = {
		isDuplicate: false,
		matchedIndex: -1,
		returnItem: {} as unknown as OrderItemInputLike,
	};

	for (let i = 0; i < itemList.length; i++) {
		const li = itemList[i];
		if (li.item.toString() !== item.item.toString()) continue;
		if (li.title !== item.title) continue;

		// Don't combine if one is master and other is not
		if (!!li.is_master !== !!item.is_master) continue;

		// Don't combine if they have different master parents
		if ((li.master_parent ?? "") !== (item.master_parent ?? "")) continue;

		const listItemOption = li.options
			.filter((o) => !o.is_hide_from_receipt)
			.map((o) => o.option)
			.sort()
			.join();
		const itemOption = item.options
			.filter((o) => !o.is_hide_from_receipt)
			.map((o) => o.option)
			.sort()
			.join();

		if (listItemOption !== itemOption) continue; //compare _ids of unhide option from receipt to get matched

		const listItemOfferId = li.subtotal_addons?.offer.metadata?._id ?? "";
		const listItemOfferTitle = li.subtotal_addons?.offer.metadata?.title ?? "";
		const listItemDiscountType = li.subtotal_addons?.discount.type ?? "";
		const listItemDiscountValue = li.subtotal_addons?.discount.value ?? "";
		const itemOfferId = item.subtotal_addons?.offer.metadata?._id ?? "";
		const itemOfferTitle = item.subtotal_addons?.offer.metadata?.title ?? "";
		const itemDiscountType = item.subtotal_addons?.discount.type ?? "";
		const itemDiscountValue = item.subtotal_addons?.discount.value ?? "";

		if (listItemOfferId.toString() !== itemOfferId.toString()) continue;
		if (listItemOfferTitle !== itemOfferTitle) continue;
		if (listItemDiscountType !== itemDiscountType) continue;
		if (listItemDiscountValue !== itemDiscountValue) continue;

		// All negative test failed return as duplicate
		response.isDuplicate = true;
		response.matchedIndex = i;
		response.returnItem = li;
		return response;
	}

	response.returnItem = item;
	return response;
};

export const minifyHTML = (htmlString: string) => {
	return (
		htmlString
			.trim()
			// Replace tabs and newlines with single spaces (preserving content spacing)
			.replace(/[\t\n\r]/g, " ")
			// Remove multiple consecutive spaces (but preserve single spaces for content)
			.replace(/ {2,}/g, " ")
			// Remove spaces around template literal expressions (but not inside content)
			.replace(/\s*\$\{\s*/g, "${")
			.replace(/\s*\}\s*/g, "}")
			// Remove spaces between HTML tags only (not content spaces)
			.replace(/>\s+</g, "><")
			// Remove whitespace immediately after opening tags and before closing tags
			.replace(/>\s+/g, ">")
			.replace(/\s+</g, "<")
			// Only remove spaces around curly braces and square brackets (most likely structural)
			// Avoid parentheses as they're often part of content like "Grand Total (USD):"
			.replace(/\s*([{}[\]])\s*/g, "$1")
			// Final cleanup of any remaining multiple spaces
			.replace(/ {2,}/g, " ")
	);
};
